import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  FormHelperText,
  Input,
  Textarea,
  Button,
  Alert,
  CircularProgress,
  Stack,
  Box,
  Divider,
  IconButton
} from '@mui/joy';
import { FloppyDisk as SaveIcon, XCircle as CancelIcon } from '@phosphor-icons/react';
import api from '../../lib/api';

interface UserProfile {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  organization?: {
    id: number;
    name: string;
  };
  is_organization_admin: boolean;
  email_signature?: string;
}

export const ProfileForm: React.FC = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    email_signature: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'danger' | 'warning'; text: string } | null>(null);

  useEffect(() => {
    fetchUserProfile();
  }, []);

  const fetchUserProfile = async () => {
    try {
      setIsLoading(true);
      const response = await api.getCurrentUser();
      setProfile(response.data);
      setFormData({
        first_name: response.data.first_name || '',
        last_name: response.data.last_name || '',
        email: response.data.email || '',
        email_signature: response.data.email_signature || ''
      });
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
      setMessage({
        type: 'danger',
        text: 'Failed to load user profile'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setMessage(null);
      
      const response = await api.updateCurrentUser(formData);
      
      setProfile(response.data);
      setIsEditing(false);
      setMessage({
        type: 'success',
        text: 'Profile updated successfully'
      });
      
      // Clear success message after 3 seconds
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Failed to update profile:', error);
      const errorMessage = error?.responseData?.email?.[0] || 
                          error?.responseData?.detail || 
                          'Failed to update profile';
      setMessage({
        type: 'danger',
        text: errorMessage
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        email_signature: profile.email_signature || ''
      });
    }
    setIsEditing(false);
    setMessage(null);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!profile) {
    return (
      <Card>
        <CardContent>
          <Alert color="danger">Failed to load user profile</Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'center' }, gap: 1, mb: 3 }}>
          <Typography level="title-lg">User Information</Typography>
          {!isEditing && (
            <Button
              variant="outlined"
              color="primary"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </Box>

        {message && (
          <Alert
            color={message.type}
            sx={{ mb: 3 }}
            endDecorator={
              <IconButton
                variant="plain"
                size="sm"
                color={message.type}
                onClick={() => setMessage(null)}
              >
                ×
              </IconButton>
            }
          >
            {message.text}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Read-only fields */}
          <FormControl>
            <FormLabel>Username</FormLabel>
            <Input
              value={profile.username}
              disabled
              sx={{ bgcolor: 'background.level1' }}
            />
          </FormControl>

          {profile.organization && (
            <FormControl>
              <FormLabel>Organization</FormLabel>
              <Input
                value={profile.organization.name}
                disabled
                sx={{ bgcolor: 'background.level1' }}
              />
            </FormControl>
          )}

          <FormControl>
            <FormLabel>Role</FormLabel>
            <Input
              value={profile.is_organization_admin ? 'Organization Admin' : 'User'}
              disabled
              sx={{ bgcolor: 'background.level1' }}
            />
          </FormControl>

          <Divider />

          {/* Editable fields */}
          <FormControl>
            <FormLabel>First Name</FormLabel>
            <Input
              value={formData.first_name}
              onChange={(e) => handleInputChange('first_name', e.target.value)}
              disabled={!isEditing || isSaving}
              placeholder="Enter your first name"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Last Name</FormLabel>
            <Input
              value={formData.last_name}
              onChange={(e) => handleInputChange('last_name', e.target.value)}
              disabled={!isEditing || isSaving}
              placeholder="Enter your last name"
            />
          </FormControl>

          <FormControl>
            <FormLabel>Email Address</FormLabel>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={!isEditing || isSaving}
              placeholder="Enter your email address"
            />
          </FormControl>

          {/*
            Saved once here instead of retyped at the bottom of every message.
            Leaving it blank falls back to the organization-wide signature set
            in Settings -> Organization.
          */}
          <FormControl>
            <FormLabel>Email Signature</FormLabel>
            <Textarea
              minRows={3}
              value={formData.email_signature}
              onChange={(e) => handleInputChange('email_signature', e.target.value)}
              disabled={!isEditing || isSaving}
              placeholder={"Jim Halpert\nProject Manager, Halpert Roofing\n(570) 555-0142"}
            />
            <FormHelperText>
              Appended to emails you send from the Inbox and from a contact's timeline.
              Leave blank to use your organization's signature.
            </FormHelperText>
          </FormControl>

          {isEditing && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2 }}>
              <Button
                variant="solid"
                color="primary"
                startDecorator={<SaveIcon />}
                onClick={handleSave}
                loading={isSaving}
                disabled={isSaving}
              >
                Save Changes
              </Button>
              <Button
                variant="outlined"
                color="neutral"
                startDecorator={<CancelIcon />}
                onClick={handleCancel}
                disabled={isSaving}
              >
                Cancel
              </Button>
            </Stack>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};